import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import React from "react";
import { Link as RouterLink } from "react-router-dom";

const INSTRUCTOR = {
  nameEn: "Sky Gallery & Art Center",
  nameZh: "石开画廊与艺术中心",
  address: "1 N Tyson Ave., 2nd Floor, Floral Park NY 11001",
};

const AboutSection: React.FC = () => {
  return (
    <Box
      id="about"
      sx={{
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#d6ecf7",
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 8, lg: 12 },
        borderTop: "1px solid rgba(15,25,41,0.12)",
      }}
    >
      {/* Gothic triangle wallpaper — 6 solid triangles per 180×180 tile, mixed directions */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cpolygon points='50,30 59,46 41,46' fill='%230f1929' fill-opacity='0.1'/%3E%3Cpolygon points='155,70 139,61 139,79' fill='%230f1929' fill-opacity='0.1'/%3E%3Cpolygon points='80,158 89,142 71,142' fill='%230f1929' fill-opacity='0.1'/%3E%3Cpolygon points='20,130 36,121 36,139' fill='%230f1929' fill-opacity='0.1'/%3E%3Cpolygon points='148,125 157,141 139,141' fill='%230f1929' fill-opacity='0.1'/%3E%3Cpolygon points='110,60 119,44 101,44' fill='%230f1929' fill-opacity='0.1'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "180px 180px",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 5, md: 10 },
          alignItems: { xs: "flex-start", md: "center" },
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ flex: "1 1 55%" }}
        >
          <Box
            sx={{ width: 40, height: 2, backgroundColor: "#c9a84c", mb: 3 }}
          />
          <Typography
            variant="h2"
            sx={{
              mb: 3,
              fontSize: { xs: "2rem", md: "3rem" },
              color: "#0f1929",
            }}
          >
            About the Artist
          </Typography>
          <Typography
            data-testid="about-body"
            variant="body1"
            sx={{
              color: "#2c4a5e",
              lineHeight: 1.9,
              fontFamily: '"Inter", sans-serif',
              fontSize: { xs: "0.95rem", md: "1rem" },
              mb: 2,
            }}
          >
            Kexin (Kathy) Su is a talented visual artist currently attending
            Garden City High School. Her creative work is deeply influenced by
            her rich cultural heritage and academic dedication to the arts.
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, mb: 2, color: "#2c4a5e", fontFamily: '"Inter", sans-serif', fontSize: { xs: "0.95rem", md: "1rem" }, lineHeight: 1.9 }}>
            <li>
              <strong>Multiple Art Prizes</strong> — recognized for outstanding artistic talent through competitive awards.
            </li>
            <li>
              <strong>MoMA Exhibition</strong> — artwork selected and displayed at the Museum of Modern Art.
            </li>
            <li>
              <strong>National Art Honor Society</strong> — inducted in recognition of sustained excellence and dedication to the visual arts.
            </li>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "#2c4a5e",
              lineHeight: 1.9,
              fontFamily: '"Inter", sans-serif',
              fontSize: { xs: "0.95rem", md: "1rem" },
            }}
          >
            Kexin (Kathy) Su is an exceptionally talented young artist whose work has
            already earned recognition at the national and international level.
            Her passion for art, combined with her cultural perspective,
            positions her as a distinctive and promising creative voice.
          </Typography>

          <Box sx={{ mt: 4 }}>
            <Box
              component={RouterLink}
              to="/contact"
              data-testid="about-contact-link"
              sx={{
                display: 'inline-block',
                color: '#0f1929',
                fontFamily: '"Inter", sans-serif',
                fontSize: '0.8rem',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                borderBottom: '1px solid #c9a84c',
                pb: 0.25,
                '&:hover': { color: '#c9a84c' },
                transition: 'color 0.2s',
              }}
            >
              Get in touch →
            </Box>
          </Box>

          <Box
            sx={{ mt: 4, pt: 3, borderTop: "1px solid rgba(15,25,41,0.12)" }}
          >
            <Typography
              variant="caption"
              data-testid="instructor-label"
              sx={{
                color: "#c9a84c",
                fontFamily: '"Inter", sans-serif',
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                display: "block",
                mb: 0.5,
              }}
            >
              Instructor
            </Typography>
            <Typography
              variant="body2"
              data-testid="instructor-name"
              sx={{
                color: "#0f1929",
                fontFamily: '"Inter", sans-serif',
                mb: 0.25,
              }}
            >
              {INSTRUCTOR.nameEn}
            </Typography>
            <Typography
              variant="body2"
              data-testid="instructor-name-zh"
              sx={{
                color: "#2c4a5e",
                fontFamily: '"Inter", sans-serif',
                mb: 0.25,
              }}
            >
              {INSTRUCTOR.nameZh}
            </Typography>
            <Typography
              variant="caption"
              data-testid="instructor-address"
              sx={{
                color: "#2c4a5e",
                fontFamily: '"Inter", sans-serif',
                display: "block",
              }}
            >
              {INSTRUCTOR.address}
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
          style={{ flex: "0 0 auto" }}
        >
          <Box
            sx={{
              width: { xs: "180px", md: "220px" },
              outline: "1px solid #c9a84c",
              outlineOffset: "8px",
              overflow: "hidden",
            }}
          >
            <img
              src="/images/my-photo.jpg"
              alt="Kexin (Kathy) Su"
              style={{ width: "100%", display: "block" }}
            />
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default AboutSection;
